// Sender avatar utilities — deterministic color + initial from email/name.
// Icon resolution chain (2026-08-24, per Brian):
//   1. OVERRIDES — exact email / whole-domain table below (emoji / image URL) wins over all
//   2. GRAVATAR  — personal avatars keyed on md5(email); d=404 so misses fail cleanly
//   3. DUCKDUCKGO icon service — usually higher quality than S2
//   4. GOOGLE S2 favicon service
//   5. DOMAIN /favicon.ico direct
//   6. Colored circle with sender's initial (deterministic hash color)
// Fallbacks chain via <img onError> — each failure advances to the next source.

import { useState } from 'react';

const PALETTE = [
  '#e57373', '#f06292', '#ba68c8', '#7986cb',
  '#5c9ce6', '#4db6ac', '#66bb6a', '#9ccc65',
  '#ffa726', '#ff8a65', '#a1887f', '#78909c',
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function avatarColor(email: string): string {
  return PALETTE[hashString(email.toLowerCase()) % PALETTE.length];
}

export function avatarInitial(name: string | null, email: string | null): string {
  const source = (name && name.trim()) || email || '?';
  const cleaned = source.replace(/^(noreply|no-reply|info|admin|support|notifications?)@/i, '');
  const ch = cleaned.charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

function extractDomain(email: string | null): string | null {
  if (!email || !email.includes('@')) return null;
  return email.split('@')[1]?.toLowerCase() || null;
}

/* ---------- MD5 (Gravatar requires it; SubtleCrypto only does SHA) ---------- */

function add32(a: number, b: number): number { return (a + b) | 0; }
function cmn(q: number, a: number, b: number, x: number, s: number, t: number): number {
  a = add32(add32(a, q), add32(x, t));
  return add32((a << s) | (a >>> (32 - s)), b);
}
function ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn((b & c) | (~b & d), a, b, x, s, t); }
function gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn((b & d) | (c & ~d), a, b, x, s, t); }
function hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn(b ^ c ^ d, a, b, x, s, t); }
function ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn(c ^ (b | ~d), a, b, x, s, t); }

function md5cycle(x: number[], k: number[]): void {
  let a = x[0], b = x[1], c = x[2], d = x[3];
  a = ff(a, b, c, d, k[0], 7, -680876936);
  d = ff(d, a, b, c, k[1], 12, -389564586);
  c = ff(c, d, a, b, k[2], 17, 606105819);
  b = ff(b, c, d, a, k[3], 22, -1044525330);
  a = ff(a, b, c, d, k[4], 7, -176418897);
  d = ff(d, a, b, c, k[5], 12, 1200080426);
  c = ff(c, d, a, b, k[6], 17, -1473231341);
  b = ff(b, c, d, a, k[7], 22, -45705983);
  a = ff(a, b, c, d, k[8], 7, 1770035416);
  d = ff(d, a, b, c, k[9], 12, -1958414417);
  c = ff(c, d, a, b, k[10], 17, -42063);
  b = ff(b, c, d, a, k[11], 22, -1990404162);
  a = ff(a, b, c, d, k[12], 7, 1804603682);
  d = ff(d, a, b, c, k[13], 12, -40341101);
  c = ff(c, d, a, b, k[14], 17, -1502002290);
  b = ff(b, c, d, a, k[15], 22, 1236535329);

  a = gg(a, b, c, d, k[1], 5, -165796510);
  d = gg(d, a, b, c, k[6], 9, -1069501632);
  c = gg(c, d, a, b, k[11], 14, 643717713);
  b = gg(b, c, d, a, k[0], 20, -373897302);
  a = gg(a, b, c, d, k[5], 5, -701558691);
  d = gg(d, a, b, c, k[10], 9, 38016083);
  c = gg(c, d, a, b, k[15], 14, -660478335);
  b = gg(b, c, d, a, k[4], 20, -405537848);
  a = gg(a, b, c, d, k[9], 5, 568446438);
  d = gg(d, a, b, c, k[14], 9, -1019803690);
  c = gg(c, d, a, b, k[3], 14, -187363961);
  b = gg(b, c, d, a, k[8], 20, 1163531501);
  a = gg(a, b, c, d, k[13], 5, -1444681467);
  d = gg(d, a, b, c, k[2], 9, -51403784);
  c = gg(c, d, a, b, k[7], 14, 1735328473);
  b = gg(b, c, d, a, k[12], 20, -1926607734);

  a = hh(a, b, c, d, k[5], 4, -378558);
  d = hh(d, a, b, c, k[8], 11, -2022574463);
  c = hh(c, d, a, b, k[11], 16, 1839030562);
  b = hh(b, c, d, a, k[14], 23, -35309556);
  a = hh(a, b, c, d, k[1], 4, -1530992060);
  d = hh(d, a, b, c, k[4], 11, 1272893353);
  c = hh(c, d, a, b, k[7], 16, -155497632);
  b = hh(b, c, d, a, k[10], 23, -1094730640);
  a = hh(a, b, c, d, k[13], 4, 681279174);
  d = hh(d, a, b, c, k[0], 11, -358537222);
  c = hh(c, d, a, b, k[3], 16, -722521979);
  b = hh(b, c, d, a, k[6], 23, 76029189);
  a = hh(a, b, c, d, k[9], 4, -640364487);
  d = hh(d, a, b, c, k[12], 11, -421815835);
  c = hh(c, d, a, b, k[15], 16, 530742520);
  b = hh(b, c, d, a, k[2], 23, -995338651);

  a = ii(a, b, c, d, k[0], 6, -198630844);
  d = ii(d, a, b, c, k[7], 10, 1126891415);
  c = ii(c, d, a, b, k[14], 15, -1416354905);
  b = ii(b, c, d, a, k[5], 21, -57434055);
  a = ii(a, b, c, d, k[12], 6, 1700485571);
  d = ii(d, a, b, c, k[3], 10, -1894986606);
  c = ii(c, d, a, b, k[10], 15, -1051523);
  b = ii(b, c, d, a, k[1], 21, -2054922799);
  a = ii(a, b, c, d, k[8], 6, 1873313359);
  d = ii(d, a, b, c, k[15], 10, -30611744);
  c = ii(c, d, a, b, k[6], 15, -1560198380);
  b = ii(b, c, d, a, k[13], 21, 1309151649);
  a = ii(a, b, c, d, k[4], 6, -145523070);
  d = ii(d, a, b, c, k[11], 10, -1120210379);
  c = ii(c, d, a, b, k[2], 15, 718787259);
  b = ii(b, c, d, a, k[9], 21, -343485551);

  x[0] = add32(a, x[0]);
  x[1] = add32(b, x[1]);
  x[2] = add32(c, x[2]);
  x[3] = add32(d, x[3]);
}

function md5blk(s: string): number[] {
  const blks: number[] = [];
  for (let i = 0; i < 16; i++) blks[i] = s.charCodeAt(i * 2) + (s.charCodeAt(i * 2 + 1) << 8);
  return blks;
}

function md51(s: string): number[] {
  const n = s.length;
  const state = [1732584193, -271733879, -1732584194, 271733878];
  let i: number;
  for (i = 64; i <= n; i += 64) md5cycle(state, md5blk(s.substring(i - 64, i)));
  const tail = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  const s2 = s.substring(i - 64);
  for (i = 0; i < s2.length; i++) tail[i >> 2] |= s2.charCodeAt(i) << ((i % 4) << 3);
  tail[i >> 2] |= 0x80 << ((i % 4) << 3);
  if (i > 55) {
    md5cycle(state, tail);
    for (i = 0; i < 16; i++) tail[i] = 0;
  }
  tail[14] = n * 8;
  md5cycle(state, tail);
  return state;
}

function rhex(n: number): string {
  let s = '';
  for (let j = 0; j < 4; j++) s += ((n >> (j * 8 + 4)) & 0x0f).toString(16) + ((n >> (j * 8)) & 0x0f).toString(16);
  return s;
}

function md5(s: string): string {
  return md51(s).map(rhex).join('');
}

/* ---------- overrides ---------- */

/** Exact-email overrides (lowercase). Ask Frances to edit. */
const OVERRIDES: Record<string, { emoji?: string; imageUrl?: string; color?: string }> = {
  // 'someone@example.com': { emoji: '⛵' },
  // 'other@example.com': { imageUrl: 'https://example.com/avatar.png' },
};

/** Whole-domain specials — cheaper than listing every address. */
const DOMAIN_OVERRIDES: Record<string, { emoji?: string; imageUrl?: string; color?: string }> = {
  'briannunes.com': { emoji: '🐝', color: '#faf0c8' },
};

/* ---------- components ---------- */

interface AvatarProps {
  name: string | null;
  email: string | null;
  size?: number;
}

export function SenderAvatar({ name, email, size = 36 }: AvatarProps) {
  const color = avatarColor(email || name || '?');
  const initial = avatarInitial(name, email);
  const domain = extractDomain(email);
  const emailKey = email ? email.toLowerCase() : '';
  const override = OVERRIDES[emailKey] || (domain ? DOMAIN_OVERRIDES[domain] : undefined);

  // 1. Overrides win
  if (override) {
    if (override.imageUrl) {
      return <IconImg src={override.imageUrl} size={size} color={override.color || color} initial={initial} />;
    }
    return (
      <div
        className="flex shrink-0 items-center justify-center rounded-full"
        style={{ width: size, height: size, backgroundColor: override.color || '#faf0c8', fontSize: size * 0.6 }}
      >
        {override.emoji}
      </div>
    );
  }

  // 2-6. Chain
  const chain: string[] = [];
  if (emailKey) chain.push(`https://gravatar.com/avatar/${md5(emailKey)}?d=404&s=64`);
  if (domain) {
    chain.push(`https://icons.duckduckgo.com/ip3/${domain}.ico`);
    chain.push(`https://www.google.com/s2/favicons?domain=${domain}&sz=64`);
    chain.push(`https://${domain}/favicon.ico`);
  }
  if (chain.length === 0) return <InitialCircle initial={initial} color={color} size={size} />;
  return <IconChain chain={chain} size={size} color={color} initial={initial} />;
}

function InitialCircle({ initial, color, size }: { initial: string; color: string; size: number }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.42, lineHeight: 1 }}
    >
      {initial}
    </div>
  );
}

function IconImg({ src, size, color, initial }: { src: string; size: number; color: string; initial: string }) {
  const [ok, setOk] = useState(true);
  if (!ok) return <InitialCircle initial={initial} color={color} size={size} />;
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full overflow-hidden"
      style={{ width: size, height: size, backgroundColor: color }}
    >
      <img src={src} alt="" width={size} height={size} className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
    </div>
  );
}

/** Walks the source chain: renders an <img>; onError advances to the next source. */
function IconChain({ chain, size, color, initial }: { chain: string[]; size: number; color: string; initial: string }) {
  const [idx, setIdx] = useState(0);
  const src = chain[idx];
  if (!src) return <InitialCircle initial={initial} color={color} size={size} />;
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full overflow-hidden"
      style={{ width: size, height: size, backgroundColor: color }}
    >
      <img
        key={src}
        src={src}
        alt=""
        width={size}
        height={size}
        className="h-full w-full object-cover"
        onError={() => setIdx(i => i + 1)}
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}
